// Dynamic colour theme runtime (docs/CONTENT_CONTRACT.md "Theme").
//
// The palette lives in CSS variables (`--c-<token>: R G B`) defined by index.css. The admin can override
// any token for light and dark mode; the resolved hex maps come from GET /api/site-config `theme`.
// This module turns a theme into a `<style id="theme-vars">` block, caches it in localStorage
// (`icd-theme-vars`) so the inline script in public/index.html can apply it before first paint,
// and fires a window `icd-theme-change` event whenever the token values change (charts listen to it).
//
//   applyTheme(theme, { persist })  apply { light: {token: '#rrggbb'}, dark: {...}, radius?, updatedAt? }
//   themeToCss(theme)               the CSS string that applyTheme injects
//   syncThemeFromConfig(config)     apply site-config's theme unless a newer one was applied locally
//   hexToTriplet / tripletToHex / contrastRatio  helpers

export const THEME_STYLE_ID = 'theme-vars';
export const THEME_CACHE_KEY = 'icd-theme-vars';
export const THEME_AT_KEY = 'icd-theme-at';
export const THEME_EVENT = 'icd-theme-change';

const HEX_RE = /^#[0-9a-f]{6}$/i;
const TOKEN_RE = /^[a-z][a-z0-9-]{0,30}$/;
const RADII = {
  sharp: { card: '0.75rem', btn: '0.5rem' },
  default: { card: '1.5rem', btn: '9999px' },
  round: { card: '2rem', btn: '9999px' },
};

export function isHex(v) {
  return typeof v === 'string' && HEX_RE.test(v.trim());
}

export function hexToTriplet(hex) {
  const n = parseInt(String(hex).trim().slice(1), 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
}

export function tripletToHex(triplet) {
  const parts = String(triplet || '')
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length < 3 || parts.some((p) => !Number.isFinite(p))) return '';
  return `#${parts
    .slice(0, 3)
    .map((p) => Math.max(0, Math.min(255, Math.round(p))).toString(16).padStart(2, '0'))
    .join('')}`;
}

function luminance(hex) {
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/** WCAG 2.x contrast ratio between two '#rrggbb' colours (1–21). */
export function contrastRatio(a, b) {
  if (!isHex(a) || !isHex(b)) return 21;
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

function block(map) {
  return Object.entries(map || {})
    .filter(([k, v]) => TOKEN_RE.test(k) && isHex(v))
    .map(([k, v]) => `--c-${k}:${hexToTriplet(v)}`)
    .join(';');
}

/**
 * CSS for a theme. Selectors are `html:root` / `html.dark` so they beat index.css (`:root` / `.dark`)
 * regardless of stylesheet order, while `.dark` still beats the light block.
 */
export function themeToCss(theme) {
  if (!theme || typeof theme !== 'object') return '';
  const light = block(theme.light);
  const dark = block(theme.dark);
  const rad = RADII[theme.radius];
  const radius = rad ? `--radius-card:${rad.card};--radius-btn:${rad.btn}` : '';
  const root = [light, radius].filter(Boolean).join(';');
  return `${root ? `html:root{${root}}` : ''}${dark ? `html.dark{${dark}}` : ''}`;
}

function styleEl() {
  if (typeof document === 'undefined') return null;
  let el = document.getElementById(THEME_STYLE_ID);
  if (!el) {
    el = document.createElement('style');
    el.id = THEME_STYLE_ID;
    document.head.appendChild(el);
  }
  return el;
}

function updateMetaThemeColor(theme) {
  try {
    const dark = document.documentElement.classList.contains('dark');
    const map = (theme && (dark ? theme.dark : theme.light)) || {};
    const c = map['navy-deep'];
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta && isHex(c)) meta.setAttribute('content', c);
  } catch {
    /* cosmetic only */
  }
}

function storedAt() {
  try {
    return Number(window.localStorage.getItem(THEME_AT_KEY)) || 0;
  } catch {
    return 0;
  }
}

/**
 * Apply a theme to the document now. `persist: true` also caches it for the no-flash boot script.
 * Returns true when the CSS changed (and `icd-theme-change` was dispatched).
 */
export function applyTheme(theme, { persist = false } = {}) {
  const css = themeToCss(theme);
  const el = styleEl();
  if (!el) return false;
  const changed = el.textContent !== css;
  if (changed) el.textContent = css;
  updateMetaThemeColor(theme);
  if (persist) {
    try {
      if (css) window.localStorage.setItem(THEME_CACHE_KEY, css);
      else window.localStorage.removeItem(THEME_CACHE_KEY);
      const at = theme && theme.updatedAt ? Date.parse(theme.updatedAt) || 0 : 0;
      window.localStorage.setItem(THEME_AT_KEY, String(at));
    } catch {
      /* storage unavailable: theme still applies for this page view */
    }
  }
  if (changed && typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: { theme } }));
    } catch {
      /* very old browsers */
    }
  }
  return changed;
}

/** Apply the cached theme (used if the inline script didn't run, e.g. tests). */
export function applyCachedTheme() {
  try {
    const css = window.localStorage.getItem(THEME_CACHE_KEY);
    const el = styleEl();
    if (css && el && !el.textContent) el.textContent = css;
  } catch {
    /* ignore */
  }
}

/**
 * Apply `config.theme` from GET /api/site-config and cache it, unless this browser already applied a
 * newer theme (e.g. an admin just saved one and the site-config response is still HTTP-cached).
 */
export function syncThemeFromConfig(config) {
  const theme = config && config.theme;
  if (!theme || typeof theme !== 'object' || !theme.light) return false;
  const at = theme.updatedAt ? Date.parse(theme.updatedAt) || 0 : 0;
  if (at < storedAt()) return false;
  return applyTheme(theme, { persist: true });
}
